import React, { useState, useRef, useEffect } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { Save, Edit3, Eye, Download, FileText, Bold, Italic, Underline, List, AlignLeft, AlignCenter, AlignRight, Link, Image, Code } from 'lucide-react';

const RichTextEditor = ({ 
  value = '', 
  onChange, 
  placeholder = 'Enter contract content...', 
  readOnly = false,
  height = '400px',
  showToolbar = true,
  onSave,
  onEdit,
  onView,
  onDownload,
  title = 'Contract Content'
}) => {
  const [isEditing, setIsEditing] = useState(!readOnly);
  const [content, setContent] = useState(value);
  const quillRef = useRef(null);

  useEffect(() => {
    setContent(value);
  }, [value]);

  const handleContentChange = (newContent) => {
    setContent(newContent);
    if (onChange) {
      onChange(newContent);
    }
  };

  const handleSave = () => {
    if (onSave) {
      onSave(content);
    }
    setIsEditing(false);
  };

  const handleEdit = () => {
    setIsEditing(true);
    if (onEdit) {
      onEdit();
    }
  };

  const handleView = () => {
    setIsEditing(false);
    if (onView) {
      onView();
    }
  };

  const handleDownload = () => {
    if (onDownload) {
      onDownload(content);
    }
  };

  const modules = {
    toolbar: [
      [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ 'color': [] }, { 'background': [] }],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
      [{ 'indent': '-1'}, { 'indent': '+1' }],
      [{ 'align': [] }],
      ['link', 'image'],
      ['blockquote', 'code-block'],
      ['clean']
    ],
  };

  const formats = [
    'header', 'font', 'size',
    'bold', 'italic', 'underline', 'strike',
    'color', 'background',
    'list', 'bullet', 'indent',
    'align', 'link', 'image',
    'blockquote', 'code-block'
  ];

  const getWordCount = (htmlContent) => {
    const textContent = htmlContent.replace(/<[^>]*>/g, '');
    return textContent.trim().split(/\s+/).filter(word => word.length > 0).length;
  };

  const wordCount = getWordCount(content);

  return (
    <div style={{
      border: '1px solid #e5e7eb',
      borderRadius: '8px',
      backgroundColor: 'white',
      overflow: 'hidden'
    }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '12px 16px',
        backgroundColor: '#f9fafb',
        borderBottom: '1px solid #e5e7eb'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <FileText size={16} color="#6b7280" />
          <span style={{ fontSize: '14px', fontWeight: '500', color: '#374151' }}>
            {title}
          </span>
          <span style={{ 
            fontSize: '12px', 
            color: '#6b7280',
            backgroundColor: '#e5e7eb',
            padding: '2px 6px',
            borderRadius: '4px'
          }}>
            {wordCount} words
          </span>
        </div>
        
        <div style={{ display: 'flex', gap: '8px' }}>
          {!isEditing && (
            <button
              onClick={handleEdit}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                padding: '6px 12px',
                backgroundColor: '#3b82f6',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                fontSize: '12px',
                fontWeight: '500',
                cursor: 'pointer',
                fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif'
              }}
            >
              <Edit3 size={14} />
              Edit
            </button>
          )}
          
          {isEditing && (
            <>
              <button
                onClick={handleView}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  padding: '6px 12px',
                  backgroundColor: '#6b7280',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '12px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif'
                }}
              >
                <Eye size={14} />
                Preview
              </button>
              <button
                onClick={handleSave}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  padding: '6px 12px',
                  backgroundColor: '#10b981',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '12px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif'
                }}
              >
                <Save size={14} />
                Save
              </button>
            </>
          )}
          
          <button
            onClick={handleDownload}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              padding: '6px 12px',
              backgroundColor: '#f59e0b',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              fontSize: '12px',
              fontWeight: '500',
              cursor: 'pointer',
              fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif'
            }}
          >
            <Download size={14} />
            Export
          </button>
        </div>
      </div>

      {/* Editor */}
      <div style={{ height: height }}>
        <ReactQuill
          ref={quillRef}
          theme="snow"
          value={content}
          onChange={handleContentChange}
          placeholder={placeholder}
          readOnly={!isEditing}
          modules={isEditing ? modules : { toolbar: false }}
          formats={formats}
          style={{
            height: 'calc(100% - 42px)',
            border: 'none'
          }}
        />
      </div>

      {/* Custom Styles */}
      <style jsx="true">{`
        .ql-editor {
          font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
          font-size: 14px;
          line-height: 1.6;
          color: #374151;
        }
        
        .ql-editor h1, .ql-editor h2, .ql-editor h3, .ql-editor h4, .ql-editor h5, .ql-editor h6 {
          font-weight: 600;
          color: #1f2937;
          margin-top: 1.5em;
          margin-bottom: 0.5em;
        }
        
        .ql-editor h1 { font-size: 24px; }
        .ql-editor h2 { font-size: 20px; }
        .ql-editor h3 { font-size: 18px; }
        .ql-editor h4 { font-size: 16px; }
        .ql-editor h5 { font-size: 14px; }
        .ql-editor h6 { font-size: 12px; }
        
        .ql-editor p {
          margin-bottom: 1em;
        }
        
        .ql-editor ul, .ql-editor ol {
          margin: 1em 0;
          padding-left: 2em;
        }
        
        .ql-editor blockquote {
          border-left: 4px solid #e5e7eb;
          padding-left: 1em;
          margin: 1em 0;
          font-style: italic;
          color: #6b7280;
        }
        
        .ql-editor .ql-code-block {
          background-color: #f9fafb;
          border: 1px solid #e5e7eb;
          border-radius: 4px;
          padding: 1em;
          font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
          font-size: 13px;
        }
        
        .ql-toolbar {
          border-bottom: 1px solid #e5e7eb;
          background-color: #f9fafb;
        }
        
        .ql-container {
          border: none;
        }
        
        .ql-editor.ql-blank::before {
          color: #9ca3af;
          font-style: normal;
        }
      `}</style>
    </div>
  );
};

export default RichTextEditor;
